import shortid from "shortid";
import * as R from "ramda";

shortid.characters(
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ$@"
);

export const getInitialValue = message => [
  {
    id: shortid.generate(),
    message,
    type: "options",
    root: true,
    children: null
  }
];

export const addOption = R.curry((id, values) => data => {
  const newId = shortid.generate();
  const parentIndex = R.findIndex(R.propEq("id", id), data);

  return R.pipe(
    R.append({
      id: newId,
      message: values.message,
      children: null,
      root: false,
      type: "end"
    }),
    R.over(
      R.lensPath([parentIndex, "children"]),
      R.append({
        answer: values.answer,
        next_id: newId,
        tag: values.tag || undefined
      })
    )
  )(data);
});

export const addBranch = R.curry((id, values) => data => {
  return R.pipe(R.reduce((acc, option) => addOption(id, option)(acc)))(
    data,
    values
  );
});

export const removeBranch = R.curry(id => data => {
  if (data.length === 1) return;

  let optionsToCrawl = [id];
  let optionsToRemove = [];

  while (optionsToCrawl.length) {
    let [optionId, ...remainingOptionsToCrawl] = optionsToCrawl;

    const optionChildrenIds = R.pipe(
      R.find(R.propEq("id", optionId)),
      R.propOr([], "children"),
      R.pluck("next_id"),
      R.reject(R.isNil)
    )(data);

    optionsToRemove = R.flatten([optionsToRemove, optionChildrenIds]);
    optionsToCrawl = R.pipe(
      R.flatten,
      R.uniq
    )([remainingOptionsToCrawl, optionChildrenIds]);
  }

  return R.pipe(
    R.map(
      R.when(
        R.propEq("id", id),
        R.pipe(
          R.assoc("children", null),
          R.assoc("type", "end"),
          R.omit(["document", "uploadDocument"])
        )
      )
    ),
    R.reject(option => R.includes(option.id, optionsToRemove))
  )(data);
});

export const removeOption = R.curry(id => data => {
  if (data.length === 1) return;

  return R.pipe(
    R.reject(R.propEq("id", id)),
    R.map(
      R.over(
        R.lensProp("children"),
        R.unless(
          R.isNil,
          R.pipe(
            R.reject(R.propEq("next_id", id)),
            R.when(R.isEmpty, R.always(null))
          )
        )
      )
    )
  )(data);
});

export const editMessage = R.curry((id, message) =>
  R.map(R.when(R.propEq("id", id), R.assoc("message", message)))
);

export const editAnswer = R.curry((optionId, answerId, answer, tag) =>
  R.map(
    R.when(
      R.propEq("id", optionId),
      R.over(
        R.lensProp("children"),
        R.map(
          R.when(
            R.propEq("next_id", answerId),
            R.pipe(
              R.assoc("answer", answer),
              R.ifElse(R.always(tag), R.assoc("tag", tag), R.dissoc("tag"))
            )
          )
        )
      )
    )
  )
);

export const editType = (id, type) =>
  R.map(
    R.when(
      R.propEq("id", id),
      R.assoc("type", type === "document" ? "open" : type)
    )
  );

export const editOther = (id, other) =>
  R.map(R.when(R.propEq("id", id), R.mergeLeft(other)));

export const multipleActions = (
  id,
  { values, message, type, ...other }
) => data =>
  R.pipe(
    addBranch(id, values),
    editType(id, type),
    editOther(id, other),
    editMessage(id, message)
  )(data);

export const getTags = R.pipe(
  R.pluck("children"),
  R.flatten,
  R.pluck("tag"),
  R.uniq,
  R.reject(R.isNil)
);
